import { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { positionsApi, companiesApi, productsApi } from "@/services/api";
import { toast } from "sonner";
import {
  Wallet,
  Eye,
  TrendingUp,
  Loader2,
  CheckCircle,
  XCircle,
  Search,
  Plus,
  X,
  FileText,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface Position {
  id: string;
  companyId: string;
  productId: string;
  principalAmount: string;
  startDate: string;
  graceEndDate?: string;
  status: "IN_GRACE" | "ACTIVE" | "REDEEMED";
  redeemedAt?: string;
  redeemedAmount?: string;
  yieldAmount?: string;
  createdAt: string;
  company?: { razaoSocial: string };
  product?: { name: string; cdiRate: string };
}

interface YieldResult {
  principalAmount: number;
  currentAmount: number;
  yieldAmount: number;
  yieldPercentage: number;
  accumulatedFactor: number;
  businessDays: number;
  startDate: string;
  calculationDate: string;
  status: string;
  canRedeem: boolean;
  graceEndDate?: string;
}

// Mapa de yields por posição para a tabela
interface PositionYield {
  positionId: string;
  yieldAmount: number;
  accumulatedFactor: number;
  currentAmount: number;
  businessDays: number;
  loading: boolean;
}

interface Company {
  id: string;
  razaoSocial: string;
  cnpj: string;
}

interface Product {
  id: string;
  name: string;
  cdiRate: string;
  isDefault: boolean;
}


// Componente para exibir o rendimento com animação em tempo real
function AnimatedYield({
  yieldAmount,
  accumulatedFactor,
  principalAmount,
  isActive
}: {
  yieldAmount: number;
  accumulatedFactor: number;
  principalAmount: number;
  isActive: boolean;
}) {
  const [displayValue, setDisplayValue] = useState(yieldAmount);
  const baseYieldRef = useRef(yieldAmount);
  const factorRef = useRef(accumulatedFactor);
  const animationRef = useRef<number | null>(null);
  const lastUpdateRef = useRef(Date.now());

  // Calcula uma taxa de crescimento aproximada por segundo
  // Baseado no CDI médio diário (aproximadamente 0.0004 ou 0.04% ao dia)
  // Dividido por 86400 segundos = taxa por segundo
  const getGrowthRatePerSecond = useCallback(() => {
    // Taxa CDI diária média aproximada = 0.00043 (baseado em CDI de ~12% a.a.)
    // Taxa por segundo considerando 252 dias úteis e 8h de mercado
    // Na prática, para efeito visual, usamos uma aproximação que mostra movimento
    const dailyRate = 0.00043 * 0.95; // 95% do CDI
    const secondsPerBusinessDay = 8 * 60 * 60; // 8 horas de mercado
    return dailyRate / secondsPerBusinessDay;
  }, []);

  useEffect(() => {
    baseYieldRef.current = yieldAmount;
    factorRef.current = accumulatedFactor;
    setDisplayValue(yieldAmount);
  }, [yieldAmount, accumulatedFactor]);

  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const animate = () => {
      const now = Date.now();
      const elapsed = (now - lastUpdateRef.current) / 1000; // segundos
      lastUpdateRef.current = now;

      // Incrementa o valor baseado na taxa de crescimento
      const ratePerSecond = getGrowthRatePerSecond();
      const increment = principalAmount * factorRef.current * ratePerSecond * elapsed;

      setDisplayValue(prev => prev + increment);

      animationRef.current = requestAnimationFrame(animate);
    };

    lastUpdateRef.current = Date.now();
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, principalAmount, getGrowthRatePerSecond]);

  // Formata com 4 casas decimais
  const formattedValue = displayValue.toFixed(4);

  return (
    <span className="font-mono text-green-600 dark:text-green-400 tabular-nums">
      +R$ {formattedValue}
    </span>
  );
}

export function PositionsPage() {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Yields para a tabela
  const [positionYields, setPositionYields] = useState<Map<string, PositionYield>>(new Map());
  const [loadingYields, setLoadingYields] = useState(false);

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [yieldInfo, setYieldInfo] = useState<YieldResult | null>(null);
  const [loadingYield, setLoadingYield] = useState(false);
  const [confirmRedeem, setConfirmRedeem] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [createForm, setCreateForm] = useState({
    companyId: "",
    productId: "",
    amountBRL: "", // Input em reais para facilitar
  });
  const [creating, setCreating] = useState(false);

  // Result feedback
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Extrato PDF
  const [downloadingExtrato, setDownloadingExtrato] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    loadFormData();
  }, []);

  async function loadFormData() {
    try {
      const [companiesData, productsData] = await Promise.all([
        companiesApi.list(),
        productsApi.listActive(),
      ]);
      setCompanies(companiesData);
      setProducts(productsData);

      // Define produto padrão
      const defaultProduct = productsData.find((p: Product) => p.isDefault);
      if (defaultProduct) {
        setCreateForm(prev => ({ ...prev, productId: defaultProduct.id }));
      }
    } catch (error) {
      console.error("Erro ao carregar dados do formulário:", error);
    }
  }

  // Carrega os yields de todas as posições ativas
  const loadPositionYields = useCallback(async (positionsData: Position[]) => {
    const activePositions = positionsData.filter(p => p.status !== "REDEEMED");
    if (activePositions.length === 0) return;

    setLoadingYields(true);

    const newYields = new Map<string, PositionYield>();

    // Carrega yields em paralelo
    await Promise.all(
      activePositions.map(async (position) => {
        try {
          const yieldData = await positionsApi.getYield(position.id);
          newYields.set(position.id, {
            positionId: position.id,
            yieldAmount: yieldData.yieldAmount,
            accumulatedFactor: yieldData.accumulatedFactor,
            currentAmount: yieldData.currentAmount,
            businessDays: yieldData.businessDays,
            loading: false,
          });
        } catch (error) {
          console.error(`Erro ao carregar yield da posição ${position.id}:`, error);
          newYields.set(position.id, {
            positionId: position.id,
            yieldAmount: 0,
            accumulatedFactor: 1,
            currentAmount: parseFloat(position.principalAmount),
            businessDays: 0,
            loading: false,
          });
        }
      })
    );

    setPositionYields(newYields);
    setLoadingYields(false);
  }, []);

  async function loadData() {
    try {
      const positionsData = await positionsApi.list();
      setPositions(positionsData);

      // Carrega os yields das posições ativas
      await loadPositionYields(positionsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function openDetailModal(position: Position) {
    setSelectedPosition(position);
    setYieldInfo(null);
    setShowDetailModal(true);

    if (position.status !== "REDEEMED") {
      setLoadingYield(true);
      try {
        const yieldData = await positionsApi.getYield(position.id);
        setYieldInfo(yieldData);
      } catch (error) {
        console.error("Erro ao calcular rendimento:", error);
      } finally {
        setLoadingYield(false);
      }
    }
  }

  async function handleRedeem() {
    if (!selectedPosition) return;

    setRedeeming(true);
    try {
      await positionsApi.redeem(selectedPosition.id);
      setConfirmRedeem(false);
      setShowDetailModal(false);
      setSelectedPosition(null);
      setResult({ success: true, message: "Resgate realizado com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao resgatar",
      });
    } finally {
      setRedeeming(false);
    }
  }

  async function handleCreate() {
    if (!createForm.companyId || !createForm.amountBRL) {
      setResult({ success: false, message: "Preencha todos os campos obrigatórios" });
      return;
    }

    const amountBRL = parseFloat(createForm.amountBRL.replace(/\./g, "").replace(",", "."));
    if (isNaN(amountBRL) || amountBRL <= 0) {
      setResult({ success: false, message: "Valor inválido" });
      return;
    }

    // Converte para centavos
    const amountCents = Math.round(amountBRL * 100);

    setCreating(true);
    try {
      await positionsApi.create({
        companyId: createForm.companyId,
        productId: createForm.productId || undefined,
        amountCents,
      });
      setShowCreateModal(false);
      setCreateForm({ companyId: "", productId: products.find(p => p.isDefault)?.id || "", amountBRL: "" });
      setResult({ success: true, message: "Posição criada com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao criar posição",
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleDownloadExtrato(companyId: string, companyName: string) {
    setDownloadingExtrato(companyId);
    try {
      const blob = await positionsApi.getExtratoPdf(companyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `extrato-aplicacao-${companyName.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erro ao baixar extrato", {
        description: error instanceof Error ? error.message : "Tente novamente",
      });
    } finally {
      setDownloadingExtrato(null);
    }
  }

  function formatInputCurrency(value: string): string {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, "");
    if (!numbers) return "";

    // Converte para número e formata
    const amount = parseInt(numbers, 10) / 100;
    return amount.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Filter positions
  const filteredPositions = positions.filter(
    (p) =>
      p.company?.razaoSocial?.toLowerCase().includes(search.toLowerCase()) ||
      p.product?.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalPrincipal = positions
    .filter((p) => p.status !== "REDEEMED")
    .reduce((sum, p) => sum + parseFloat(p.principalAmount), 0);
  const activeCount = positions.filter((p) => p.status === "ACTIVE").length;
  const inGraceCount = positions.filter((p) => p.status === "IN_GRACE").length;

  // Calcula o rendimento total de todas as posições vigentes
  const totalYield = Array.from(positionYields.values()).reduce(
    (sum, py) => sum + py.yieldAmount,
    0
  );

  function getStatusBadge(status: string) {
    switch (status) {
      case "IN_GRACE":
        return <Badge variant="warning">Em Carência</Badge>;
      case "ACTIVE":
        return <Badge variant="success">Ativo</Badge>;
      case "REDEEMED":
        return <Badge variant="secondary">Resgatado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Posições"
          description="Gerencie as alocações de investimento das empresas"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Posições"
        description="Gerencie as alocações de investimento das empresas"
        stats={[
          { value: formatCurrency(totalPrincipal), label: "Total Alocado" },
          { value: `+${formatCurrency(totalYield)}`, label: "Rendimento Total" },
          { value: activeCount, label: "Ativas" },
          { value: inGraceCount, label: "Em Carência" },
        ]}
        actions={
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Posição
          </Button>
        }
      />

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por empresa ou produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Result feedback */}
      {result && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            result.success
              ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {result.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/* Positions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista de Posições</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPositions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {search
                ? "Nenhuma posição encontrada com esse filtro."
                : "Nenhuma posição cadastrada ainda."}
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Empresa</th>
                    <th className="text-left p-4 font-medium text-sm">Produto</th>
                    <th className="text-right p-4 font-medium text-sm">
                      Principal
                    </th>
                    <th className="text-right p-4 font-medium text-sm">
                      Rendimento
                    </th>
                    <th className="text-left p-4 font-medium text-sm">Início</th>
                    <th className="text-left p-4 font-medium text-sm">Status</th>
                    <th className="text-right p-4 font-medium text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPositions.map((position) => {
                    const positionYield = positionYields.get(position.id);
                    const isActive = position.status !== "REDEEMED";

                    return (
                    <tr key={position.id} className="hover:bg-muted/50">
                      <td className="p-4 text-sm font-medium">
                        {position.company?.razaoSocial || "—"}
                      </td>
                      <td className="p-4 text-sm">
                        {position.product?.name || "—"}
                        <span className="text-xs text-muted-foreground ml-1">
                          ({parseFloat(position.product?.cdiRate || "0").toFixed(0)}% CDI)
                        </span>
                      </td>
                      <td className="p-4 text-sm text-right font-medium">
                        {formatCurrency(parseFloat(position.principalAmount))}
                      </td>
                      <td className="p-4 text-sm text-right">
                        {position.status === "REDEEMED" ? (
                          <span className="font-mono text-muted-foreground">
                            +R$ {parseFloat(position.yieldAmount || "0").toFixed(4)}
                          </span>
                        ) : loadingYields || !positionYield ? (
                          <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                        ) : (
                          <AnimatedYield
                            yieldAmount={positionYield.yieldAmount}
                            accumulatedFactor={positionYield.accumulatedFactor}
                            principalAmount={parseFloat(position.principalAmount)}
                            isActive={isActive}
                          />
                        )}
                      </td>
                      <td className="p-4 text-sm">
                        {formatDate(position.startDate)}
                      </td>
                      <td className="p-4">{getStatusBadge(position.status)}</td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {isActive && position.companyId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Baixar Extrato PDF"
                              onClick={() => handleDownloadExtrato(position.companyId, position.company?.razaoSocial || "empresa")}
                              disabled={downloadingExtrato === position.companyId}
                            >
                              {downloadingExtrato === position.companyId ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailModal(position)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedPosition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Detalhes da Posição</span>
                {getStatusBadge(selectedPosition.status)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Empresa</p>
                  <p className="font-medium">
                    {selectedPosition.company?.razaoSocial}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Produto</p>
                  <p className="font-medium">
                    {selectedPosition.product?.name}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Principal</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(parseFloat(selectedPosition.principalAmount))}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data Início</p>
                  <p className="text-sm font-medium">
                    {formatDate(selectedPosition.startDate)}
                  </p>
                </div>
              </div>

              {selectedPosition.graceEndDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Fim da Carência</p>
                  <p className="text-sm font-medium">
                    {formatDate(selectedPosition.graceEndDate)}
                  </p>
                </div>
              )}

              {/* Yield Info */}
              {selectedPosition.status !== "REDEEMED" && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-medium">Rendimento Atual</span>
                    {loadingYield && (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                  </div>

                  {yieldInfo && (
                    <div className="grid grid-cols-2 gap-4 bg-muted p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Valor Atual
                        </p>
                        <p className="text-lg font-semibold text-green-600">
                          {formatCurrency(yieldInfo.currentAmount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Rendimento
                        </p>
                        <p className="text-lg font-semibold text-green-600">
                          +{formatCurrency(yieldInfo.yieldAmount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Fator Acumulado</p>
                        <p className="text-sm font-medium">
                          {yieldInfo.accumulatedFactor.toFixed(8)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Dias Úteis
                        </p>
                        <p className="text-sm font-medium">{yieldInfo.businessDays}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">
                          Rentabilidade
                        </p>
                        <p className="text-sm font-medium">
                          {yieldInfo.yieldPercentage.toFixed(4)}%
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Redeemed Info */}
              {selectedPosition.status === "REDEEMED" && (
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4 bg-muted p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Valor Resgatado
                      </p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(
                          parseFloat(selectedPosition.redeemedAmount || "0")
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Rendimento Total
                      </p>
                      <p className="text-lg font-semibold text-green-600">
                        +{formatCurrency(
                          parseFloat(selectedPosition.yieldAmount || "0")
                        )}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">
                        Data do Resgate
                      </p>
                      <p className="text-sm font-medium">
                        {selectedPosition.redeemedAt
                          ? formatDate(selectedPosition.redeemedAt)
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPosition(null);
                    setYieldInfo(null);
                  }}
                >
                  Fechar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Redeem Confirmation */}
      <ConfirmDialog
        open={confirmRedeem}
        onClose={() => setConfirmRedeem(false)}
        onConfirm={handleRedeem}
        title="Confirmar Resgate"
        description={`Deseja resgatar esta posição? O valor de ${
          yieldInfo ? formatCurrency(yieldInfo.currentAmount) : "—"
        } (principal + rendimento) será creditado para a empresa.`}
        variant="danger"
        loading={redeeming}
      />

      {/* Create Position Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Nova Posição</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company">Empresa *</Label>
                <Select
                  value={createForm.companyId}
                  onValueChange={(value) =>
                    setCreateForm((prev) => ({ ...prev, companyId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="product">Produto</Label>
                <Select
                  value={createForm.productId}
                  onValueChange={(value) =>
                    setCreateForm((prev) => ({ ...prev, productId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} ({parseFloat(product.cdiRate).toFixed(0)}% CDI)
                        {product.isDefault && " - Padrão"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Valor (R$) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    R$
                  </span>
                  <Input
                    id="amount"
                    placeholder="0,00"
                    value={createForm.amountBRL}
                    onChange={(e) => {
                      const formatted = formatInputCurrency(e.target.value);
                      setCreateForm((prev) => ({ ...prev, amountBRL: formatted }));
                    }}
                    className="pl-10 text-right font-mono"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Digite o valor em reais (ex: 100.000,00 para cem mil reais)
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    "Criar Posição"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
